import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DecimalField } from "@/components/DecimalInput";
import { intOpts, parseDecimal } from "@/lib/decimal";
import { counts, bezeichnung, datei } from "@/lib/produktion";
import { toast } from "sonner";
import { Play, CheckCircle2, Undo2, ChevronDown, History } from "lucide-react";

// aktion → [Titel, Quelle für die verfügbare Menge]
const AKTIONEN = {
  starten: ["In Produktion geben", "offen"],
  fertig: ["Als fertig melden", "in_produktion"],
  zurueck_offen: ["Korrektur: zurück auf offen", "in_produktion"],
  zurueck_produktion: ["Korrektur: fertig → in Produktion", "fertig"],
  entfaellt: ["Rest entfällt", "unversendet"],
  entfaellt_zurueck: ["Korrektur: entfällt aufheben", "entfaellt"],
};

const SEGMENTS = [["versendet", "bg-slate-700", "versendet"], ["fertig", "bg-emerald-500", "fertig"], ["in_produktion", "bg-amber-400", "in Produktion"], ["offen", "bg-slate-200", "offen"], ["entfaellt", "bg-rose-200", "entfällt"]];

export function ProduktionBar({ p, idx }) {
  const c = counts(p);
  const total = Math.max(Number(p.menge) || 0, 1);
  return (
    <div className="space-y-1">
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100" role="img" aria-label="Produktionsfortschritt">
        {SEGMENTS.map(([k, cls]) => c[k] > 0 && <div key={k} className={`${cls} transition-all`} style={{ width: `${(c[k] / total) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600" data-testid={`prod-counts-${idx}`}>
        {SEGMENTS.filter(([k]) => k !== "entfaellt" || c.entfaellt > 0).map(([k, cls, l]) => (
          <span key={k} className="inline-flex items-center gap-1" data-testid={`prod-count-${k}-${idx}`}><span className={`w-2 h-2 rounded-full ${cls}`} />{c[k]} {l}</span>
        ))}
      </div>
    </div>
  );
}

// Dialog bleibt gemountet (nur „open“ wechselt) – sonst hinterlässt Radix beim Unmount pointer-events:none am <body>.
function AktionDialog({ orderId, state, onClose, onDone }) {
  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="prod-aktion-dialog">
        {state && <AktionForm key={`${state.p.id}-${state.aktion}`} orderId={orderId} state={state} onClose={onClose} onDone={onDone} />}
      </DialogContent>
    </Dialog>
  );
}

function AktionForm({ orderId, state, onClose, onDone }) {
  const { p, aktion } = state;
  const max = counts(p)[AKTIONEN[aktion][1]];
  const [val, setText] = useState(String(max)); // Standard = alles Verfügbare
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const r = parseDecimal(val, intOpts(1, max));
    if (r.error) { setErr(r.error); return; }
    setBusy(true);
    try {
      const res = await api.post(`/auftraege/${orderId}/produktion/${p.id}`, { aktion, menge: r.value });
      onDone(res.data); toast.success(`${AKTIONEN[aktion][0]}: ${r.value} Stück`); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); } finally { setBusy(false); }
  };
  return (
    <>
        <DialogHeader><DialogTitle>{AKTIONEN[aktion][0]}</DialogTitle><DialogDescription>{bezeichnung(p)}{datei(p) && ` · ${datei(p)}`}</DialogDescription></DialogHeader>
        <Label htmlFor="prod-menge">Menge (höchstens {max})</Label>
        <DecimalField id="prod-menge" integer align="left" className="h-9" value={val} error={err} testId="prod-aktion-menge" autoFocus
          onChange={(t) => { setText(t); setErr(""); }} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="prod-aktion-submit">Übernehmen</Button>
        </DialogFooter>
    </>
  );
}

function Verlauf({ p, idx }) {
  const log = [...(p.produktion?.log || [])].reverse();
  if (!log.length) return <p className="text-xs text-slate-400">Noch keine Einträge.</p>;
  return (
    <ul className="text-xs text-slate-600 space-y-0.5 max-h-40 overflow-y-auto" data-testid={`prod-log-${idx}`}>
      {log.map((l, i) => <li key={i}><span className="font-mono text-slate-400">{new Date(l.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span> · {l.user || "–"} · {l.text} · <b>{l.menge}</b></li>)}
    </ul>
  );
}

export function ProduktionRow({ p, idx, orderId, canEdit, canDrop, onChanged }) {
  const [dlg, setDlg] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const c = counts(p);
  const open = (aktion) => setDlg({ p, aktion });
  const corrections = ["zurueck_offen", "zurueck_produktion", ...(canDrop ? ["entfaellt", "entfaellt_zurueck"] : [])].filter((a) => counts(p)[AKTIONEN[a][1]] > 0);
  return (
    <li className="py-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] md:items-center" data-testid={`prod-row-${idx}`}>
      <div className="min-w-0">
        <div className="font-medium text-slate-900 truncate">{bezeichnung(p)} <span className="font-mono text-xs text-slate-500">× {p.menge}</span></div>
        {datei(p) && <div className="text-xs text-slate-500 truncate" data-testid={`prod-datei-${idx}`}>{datei(p)}</div>}
      </div>
      <ProduktionBar p={p} idx={idx} />
      <div className="flex items-center gap-1.5 flex-wrap md:justify-end">
        {canEdit && c.offen > 0 && <Button size="sm" variant="outline" onClick={() => open("starten")} data-testid={`prod-start-${idx}`}><Play className="w-3.5 h-3.5 mr-1" /> In Produktion</Button>}
        {canEdit && c.in_produktion > 0 && <Button size="sm" onClick={() => open("fertig")} data-testid={`prod-fertig-${idx}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Fertig melden</Button>}
        {canEdit && corrections.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid={`prod-korrektur-${idx}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Korrektur <ChevronDown className="w-3 h-3 ml-0.5" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">{corrections.map((a) => <DropdownMenuItem key={a} onSelect={() => open(a)} data-testid={`prod-${a}-${idx}`}>{AKTIONEN[a][0]}</DropdownMenuItem>)}</DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowLog((x) => !x)} aria-expanded={showLog} aria-label="Verlauf anzeigen" data-testid={`prod-log-toggle-${idx}`}><History className="w-3.5 h-3.5" /></Button>
      </div>
      {showLog && <div className="md:col-span-3 pl-1"><Verlauf p={p} idx={idx} /></div>}
      <AktionDialog orderId={orderId} state={dlg} onClose={() => setDlg(null)} onDone={onChanged} />
    </li>
  );
}
